import React, { useCallback, useEffect } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import { useAdminStore } from "../../Store/Admin";
import { useParams } from "react-router-dom";
import Table, { Column, DataSourceItem } from "../../Components/Atoms/Table";
import Tag from "../../Components/Atoms/Tag";
import { ReactComponent as KebabMenuIcon } from "../../Assets/ActionIcons/KebabMenuIcon.svg";
import { User } from "../../Contracts/User";
import { toLocalDateString } from "../../Utils/DateUtils";
import LoadingPage from "../LoadingPage";
import Modal from "../../Components/Atoms/Modal";
import { capitalizeAfterSpace } from "../../Contracts/Common";
import Dropdown from "../../Components/Atoms/Dropdown";

const AdminTeamUserListPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { team_id } = useParams();
  const {
    isLoading,
    teamUsers,
    getTeamUsers,
    getOneTeam,
    teamDetails,
    disableDebugUserAccount,
    enableDebugUserAccount
  } = useAdminStore();
  const [enableDebugUserModal, setEnableDebugUserModal] = React.useState<User | undefined>(
    undefined
  );
  const [action, setAction] = React.useState<"enable" | "disable">("enable");
  const [message, setMessage] = React.useState<string | undefined>(undefined);
  const refreshData = useCallback(async () => {
    if (team_id) {
      await Promise.all([
        getOneTeam(getAccessTokenSilently, team_id),
        getTeamUsers(getAccessTokenSilently, team_id)
      ]);
    } else {
      console.error("Error fetching users");
    }
  }, [team_id, getAccessTokenSilently, getTeamUsers, getOneTeam]);
  useEffect(() => {
    refreshData();
  }, [refreshData]);
  const columns: Column<User>[] = [
    {
      title: "User Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Type",
      dataIndex: "user_type",
      key: "user_type"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: d => <Tag color={d.status === "Active" ? "lime" : "blue"}>{d.status}</Tag>
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email"
    },
    {
      title: "Date Joined",
      dataIndex: "date_joined",
      key: "date_joined"
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: d => (
        <Dropdown
          onChange={value => {
            if (value === "enable_debug_user") {
              setEnableDebugUserModal(d.data);
              setAction("enable");
            } else if (value === "disable_debug_user") {
              setEnableDebugUserModal(d.data);
              setAction("disable");
            }
          }}
          options={[
            {
              label: "Enable Debug User",
              value: "enable_debug_user"
            },
            {
              label: "Disable Debug User",
              value: "disable_debug_user"
            }
          ]}
          anchor={
            <div className="cursor-pointer flex flex-col items-center">
              <KebabMenuIcon className="text-blue-600" />
              <div className="text-blue-600">More</div>
            </div>
          }
          position="bottom-right"
        />
      )
    }
  ];

  const dataSource: DataSourceItem<User>[] = teamUsers.map(user => ({
    key: user.id.toString(),
    name: user.user_type === "user" ? `${user.first_name} ${user.last_name}` : "--",
    user_type: user.is_owner
      ? user.is_admin
        ? "Owner & Admin"
        : "Owner"
      : user.is_admin
        ? "Admin"
        : "User",
    status: user.user_type === "user" ? "Active" : "Invited",
    email: user.email,
    date_joined: user.user_type === "user" ? toLocalDateString(user.created_at) : "--",
    data: user
  }));

  return (
    <PaymentsAppLayout>
      <Modal
        isOpen={!!enableDebugUserModal}
        onClose={() => setEnableDebugUserModal(undefined)}
        onCancel={() => setEnableDebugUserModal(undefined)}
        title={`Debug user account for ${teamDetails?.name}`}
        okText={capitalizeAfterSpace(action)}
        onOk={async () => {
          if (enableDebugUserModal) {
            if (action === "enable") {
              setMessage(
                await enableDebugUserAccount(getAccessTokenSilently, enableDebugUserModal.email)
              );
            } else {
              setMessage(
                await disableDebugUserAccount(getAccessTokenSilently, enableDebugUserModal.email)
              );
            }
            setEnableDebugUserModal(undefined);
            refreshData();
          }
        }}
      >
        <div>
          Are you sure you want to <b>{action}</b> debug user for user - [
          {enableDebugUserModal?.email}]
        </div>
      </Modal>
      <Modal
        isOpen={!!message}
        onClose={() => setMessage(undefined)}
        onCancel={() => setMessage(undefined)}
        onOk={() => setMessage(undefined)}
        size="large"
      >
        <div className="text-center">{message}</div>
      </Modal>
      <div className="flex flex-col h-[calc(100vh-9rem)]">
        {!isLoading && (
          <div className="font-semibold text-lg mb-4">Users of {teamDetails?.name}</div>
        )}
        <div
          className={`flex flex-grow flex-col overflow-y-auto ${!isLoading && "border-gray-100"} `}
        >
          {isLoading && teamUsers.length === 0 ? (
            <LoadingPage />
          ) : (
            <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
          )}
        </div>
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(AdminTeamUserListPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
